// Import the correct package
import { MediaRecorder } from '@zappar/mediarecorder';
// import { getElem } from '../utils';

// const filename = 'lion-recorded-video.mp4';

class VideoRecorder {
    private mediaRecorder: MediaRecorder | null;
    private recordedChunks: Blob[];
    private isRecording: boolean;
    private recordingDuration = 0;
    private startTime = 0;
    

    constructor() {
        this.mediaRecorder = null;
        this.recordedChunks = [];
        this.isRecording = false;
    }

    async initialize(audioStream, canvas) {
        try {
            // Ensure canvas dimensions are even numbers
            this.ensureEvenCanvasDimensions(canvas);
            
            // Get the canvas stream
            const canvasStream = canvas.captureStream();
            
            // Combine audio and video streams
            const combinedStream = this.combineStreams(canvasStream, audioStream);
            
            // Create options for the recorder
            const options = {
                mimeType: 'video/mp4;codecs="avc1.4d002a"'
            };
            
            // Try to use Zappar's MediaRecorder
            try {
                this.mediaRecorder = new MediaRecorder(combinedStream, options);
                console.log('Using @zappar/mediarecorder for recording');
            } catch (error) {
                console.warn('Failed to use @zappar/mediarecorder:', error);
            }
            
            // Set up event handlers
            if (this.mediaRecorder) {
                this.mediaRecorder.ondataavailable = (event) => {
                    const { data } = event;
                    this.recordedChunks.push(data);
                };
                
                this.mediaRecorder.onstop = () => {
                    this.isRecording = false;
                    this.recordingDuration = 0;
                    console.log('Recording stopped');
                };
            }
            
            console.log('VideoRecorder initialized successfully');
            return true;
        } catch (error) {
            console.error('Error initializing VideoRecorder:', error);
            return false;
        }
    }

    // Add the missing combineStreams method
    combineStreams(videoStream, audioStream) {
        if (!audioStream) {
            return videoStream;
        }
        
        const combinedStream = new MediaStream();
        
        // Add video tracks
        videoStream.getVideoTracks().forEach(track => {
            combinedStream.addTrack(track);
        });
        
        // Add audio tracks
        audioStream.getAudioTracks().forEach(track => {
            combinedStream.addTrack(track);
        });
        
        return combinedStream;
    }

    // Add new method to ensure canvas dimensions are even numbers
    ensureEvenCanvasDimensions(canvas) {
        const width = canvas.width;
        const height = canvas.height;
        
        // Check if width is odd
        if (width % 2 !== 0) {
            canvas.width = width + 1;
            console.log(`Adjusted canvas width from ${width} to ${canvas.width} to ensure it's even`);
        }
        
        // Check if height is odd
        if (height % 2 !== 0) {
            canvas.height = height + 1;
            console.log(`Adjusted canvas height from ${height} to ${canvas.height} to ensure it's even`);
        }
    }

    startRecording() {
        if (!this.mediaRecorder || this.isRecording) {
            return false;
        }
        
        this.recordedChunks = [];
        this.startTime = Date.now();
        this.isRecording = true;
        
        // Start recording with 100ms timeslices to get frequent data chunks
        this.mediaRecorder.start(100);
        console.log('Recording started');
        return true;
    }

    stopRecording() {
        return new Promise((resolve, reject) => {
            if (!this.mediaRecorder || !this.isRecording) {
                console.warn('Cannot stop recording: recorder not active or not recording');
                // return Promise.resolve(null);
                reject();
                return;
            }
            
            // Create a one-time event handler for the stop event
            const originalOnStop = this.mediaRecorder.onstop;
                        
            this.mediaRecorder.onstop = () => {
                this.isRecording = false;
                
                // Check if we have any recorded chunks
                if (this.recordedChunks.length === 0) {
                    console.warn('No data was recorded');
                    // resolve(null);
                    reject();
                    return;
                }
                
                // Create a blob from the recorded chunks
                const blob = new Blob(this.recordedChunks, {
                    type: 'video/mp4'
                });
                
                console.log('Recording stopped, blob created:', blob, 'Size:', blob.size);
                
                // Reset chunks array
                this.recordedChunks = [];
                
                // Call the original onstop handler if it exists
                if (originalOnStop) {
                    // originalOnStop.call(this.mediaRecorder);
                }
                
                resolve(blob);
            };

            try {
                this.mediaRecorder.stop();
            } catch (error) {
                console.error('Error stopping recording:', error);
                this.isRecording = false;
                resolve(null);
            }
        })
    }

    pauseRecording() {
        if (!this.mediaRecorder || !this.isRecording || this.mediaRecorder.state === 'paused') {
            return false;
        }
        
        this.mediaRecorder.pause();
        this.recordingDuration += Date.now() - this.startTime;
        console.log('Recording paused');
        return true;
    }

    resumeRecording() {
        if (!this.mediaRecorder || !this.isRecording || this.mediaRecorder.state !== 'paused') {
            return false;
        }
        
        this.mediaRecorder.resume();
        this.startTime = Date.now();
        console.log('Recording resumed');
        return true;
    }

    getRecordingDuration() {
        if (!this.isRecording) {
            return this.recordingDuration;
        }
        
        return this.recordingDuration + (Date.now() - this.startTime);
    }

    getRecordingDurationCurrent() {
        if (!this.isRecording) {
            return this.recordingDuration;
        }
        
        return (Date.now() - this.startTime);
    }

    isSupported() {
        try {
            // Check if MediaRecorder is supported (either Zappar or native)
            return typeof MediaRecorder !== 'undefined' && 
                   MediaRecorder.isTypeSupported('video/mp4; codecs="avc1.4d002a"');
        } catch (error) {
            console.error('Error checking MediaRecorder support:', error);
            return false;
        }
    }
    
    cleanup() {
        // Clean up any resources
        if (this.mediaRecorder) {
            if (this.isRecording) {
                try {
                    this.mediaRecorder.stop();
                } catch (error) {
                    console.warn('Error stopping recorder during cleanup:', error);
                }
            }
            this.mediaRecorder = null;
        }
        
        this.recordedChunks = [];
        this.isRecording = false;
    }
}

export default VideoRecorder;